import { useState, useEffect, useCallback, useRef } from 'react'
import { fuzzySearch, sortByRelevance } from '@/lib/search-utils'

interface UseContentPaginationProps<T extends { id: string | number }> {
  fetchItems: (options?: {
    category?: string
    search?: string
    limit?: number
    offset?: number
  }) => Promise<T[]>
  fetchCategories?: () => Promise<string[]>
  initialItems?: T[]
  initialCategories?: string[]
  initialLimit?: number
  loadMoreLimit?: number
  searchDebounceMs?: number
  /** Maximum first-page candidate set used for relevance ranking. */
  searchLimit?: number
  onError?: (error: Error, context: string) => void
  maxRetries?: number
  refreshKey?: string | number | null
  /**
   * Client mode fetches the selected category without a server search term and
   * applies `processSearchResults` to the returned catalog. Use it only when the
   * fetcher returns the complete category set (the products catalog does).
   */
  searchMode?: 'server' | 'client'
  /** Optional domain-specific search and relevance strategy. */
  processSearchResults?: (items: T[], searchTerm: string) => T[]
}

interface ErrorState {
  hasError: boolean
  error: Error | null
  context: string
  retryCount: number
}

function defaultProcessSearchResults<T extends { id: string | number }>(
  results: T[],
  value: string
): T[] {
  // Tokenized to mirror the server filter in lib/directus.ts. Matching the whole
  // phrase here rejected every multi-word result the server had just returned,
  // because no single field contained the words adjacently.
  const tokens = value.trim().toLowerCase().split(/\s+/).filter(Boolean)
  if (tokens.length === 0) return results

  const fuzzyFilteredResults = results.filter((item: any) => {
    const title = item.title || item.name || item.question || ''
    const excerpt = item.excerpt || item.description || ''
    const content = item.content || ''
    const haystack = `${title} ${excerpt} ${content}`.toLowerCase()

    return tokens.every(token =>
      haystack.includes(token) ||
      fuzzySearch(token, title, 2) ||
      fuzzySearch(token, excerpt, 2)
    )
  })

  return sortByRelevance(fuzzyFilteredResults as any[], value) as T[]
}

export function useContentPagination<T extends { id: string | number }>({
  fetchItems,
  fetchCategories,
  initialItems,
  initialCategories,
  initialLimit = 12,
  loadMoreLimit = 12,
  searchDebounceMs = 500,
  searchLimit = 25,
  onError,
  maxRetries = 3,
  refreshKey = null,
  searchMode = 'server',
  processSearchResults
}: UseContentPaginationProps<T>) {
  const hasInitialCategories = Boolean(initialCategories?.length)
  const [items, setItems] = useState<T[]>(Array.isArray(initialItems) ? initialItems : [])
  const [categories, setCategories] = useState<string[]>(initialCategories || [])
  const [selectedCategory, setSelectedCategory] = useState<string>("All")
  const [searchTerm, setSearchTerm] = useState("")
  const [isSearching, setIsSearching] = useState(false)
  const [visibleItems, setVisibleItems] = useState(initialLimit)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [isLoading, setIsLoading] = useState(!Array.isArray(initialItems) || initialItems.length === 0)
  const [isLoadingCategories, setIsLoadingCategories] = useState(!hasInitialCategories && Boolean(fetchCategories))
  const [hasMoreItems, setHasMoreItems] = useState(false)
  const [errorState, setErrorState] = useState<ErrorState>({
    hasError: false,
    error: null,
    context: '',
    retryCount: 0
  })
  
  // Debounce ref for search
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined)
  // Every top-level query receives an id. Only the latest request may commit,
  // preventing a slow search response from overwriting a newer category choice.
  const latestQueryIdRef = useRef(0)
  
  // Track if we've already initialized to prevent re-running when initialItems/initialCategories change
  const hasInitializedRef = useRef(false)
  const categoriesInitializedRef = useRef(false)
  const previousRefreshKeyRef = useRef<string | number | null | symbol>(Symbol('unset'))
  
  // Retry mechanism with exponential backoff
  const retryWithBackoff = useCallback(async <R,>(
    fn: () => Promise<R>,
    context: string,
    retryCount = 0,
    isActive: () => boolean = () => true
  ): Promise<R> => {
    try {
      const result = await fn()
      if (!isActive()) return result
      // Clear any stale error state on success so new queries can recover cleanly
      setErrorState(prev => {
        if (prev.hasError) {
          return { hasError: false, error: null, context: '', retryCount: 0 }
        }
        return prev
      })
      return result
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error))

      // A newer query superseded this one. Do not retry it or let it surface a
      // stale error over the current results.
      if (!isActive()) {
        throw err
      }
      
      if (retryCount < maxRetries) {
        // Exponential backoff: 1s, 2s, 4s
        const delay = Math.pow(2, retryCount) * 1000
        await new Promise(resolve => setTimeout(resolve, delay))
        
        if (!isActive()) throw err
        return retryWithBackoff(fn, context, retryCount + 1, isActive)
      }
      
      // Max retries reached
      setErrorState({
        hasError: true,
        error: err,
        context,
        retryCount
      })
      
      if (onError) {
        onError(err, context)
      }
      
      throw err
    }
  }, [maxRetries, onError])

  // Use refs to store fetch functions to avoid dependency issues
  const fetchItemsRef = useRef(fetchItems)
  const fetchCategoriesRef = useRef(fetchCategories)
  const processSearchResultsRef = useRef(processSearchResults)
  
  // Update refs when functions change
  useEffect(() => {
    fetchItemsRef.current = fetchItems
  }, [fetchItems])
  
  useEffect(() => {
    fetchCategoriesRef.current = fetchCategories
  }, [fetchCategories])

  useEffect(() => {
    processSearchResultsRef.current = processSearchResults
  }, [processSearchResults])

  // Category data has its own recovery path. An empty SSR array means the
  // server did not provide usable categories, so retry from the client.
  useEffect(() => {
    if (categoriesInitializedRef.current) return

    // Mark the request as started before any state update can trigger another
    // render. This keeps an empty or unstable initialCategories prop from
    // launching duplicate recovery requests while the first one is pending.
    categoriesInitializedRef.current = true

    if (hasInitialCategories) {
      setCategories(initialCategories!)
      setIsLoadingCategories(false)
      return
    }

    if (!fetchCategoriesRef.current) {
      setIsLoadingCategories(false)
      return
    }

    setIsLoadingCategories(true)
    retryWithBackoff(
      () => fetchCategoriesRef.current!(),
      'initial-load-categories'
    ).then((categoriesData) => {
      setCategories(categoriesData)
    }).catch((error) => {
      console.error('Error loading categories:', error)
    }).finally(() => {
      setIsLoadingCategories(false)
    })
  }, [hasInitialCategories, initialCategories, retryWithBackoff])

  // Load initial data - only run once on mount, skip if initialItems provided
  useEffect(() => {
    // Prevent re-running if we've already initialized
    if (hasInitializedRef.current) {
      // If initialItems changed from empty array to actual data (Suspense resolved), update items
      if (Array.isArray(initialItems) && initialItems.length > 0 && items.length === 0) {
        setItems(initialItems)
        setVisibleItems(initialLimit)
        // If we received at least the initial limit, assume more could be available
        setHasMoreItems(initialItems.length >= initialLimit)
        setIsLoading(false)
        if (hasInitialCategories) {
          setCategories(initialCategories!)
        }
      }
      return
    }

    // If initialItems are provided with actual data, use them and skip fetching
    // Empty array means "should fetch client-side", undefined means "not provided, should fetch"
    if (Array.isArray(initialItems) && initialItems.length > 0) {
      setItems(initialItems)
      setVisibleItems(initialLimit)
      // If we received at least the initial limit, assume more could be available
      setHasMoreItems(initialItems.length >= initialLimit)
      setIsLoading(false)
      hasInitializedRef.current = true
      return
    }

    // If initialItems is undefined, fetch data normally (for client-side pages like products)

    // Otherwise, fetch data as before
    const loadInitialData = async () => {
      const requestId = ++latestQueryIdRef.current
      const isActive = () => latestQueryIdRef.current === requestId
      setIsLoading(true)
      setErrorState({ hasError: false, error: null, context: '', retryCount: 0 })
      
      try {
        // Load items first (categories only if not already provided)
        const itemsData = await retryWithBackoff(
          () => fetchItemsRef.current({ limit: 25 }),
          'initial-load-items',
          0,
          isActive
        )

        if (!isActive()) return
        setItems(itemsData)
        setVisibleItems(initialLimit)
        // If we received at least the initial limit, assume more could be available
        setHasMoreItems(itemsData.length >= initialLimit)
        
      } catch (error) {
        console.error('Error loading initial data:', error)
        // Error state is set by retryWithBackoff
      } finally {
        if (isActive()) setIsLoading(false)
        hasInitializedRef.current = true
      }
    }

    loadInitialData()
    // initialItems/initialCategories/items.length intentionally excluded — hasInitializedRef
    // gates this to a single run; including them would re-fetch on every prop/state change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryWithBackoff, initialLimit])

  const executeQuery = useCallback(async ({
    category,
    search,
    context,
    requestId,
    showLoading = false,
  }: {
    category: string
    search: string
    context: string
    requestId: number
    showLoading?: boolean
  }) => {
    const normalizedSearch = search.trim()
    const queryLimit = normalizedSearch ? searchLimit : 25
    const isActive = () => latestQueryIdRef.current === requestId

    if (isActive()) setIsLoadingMore(false)
    if (showLoading) setIsLoading(true)

    try {
      const queryItems = await retryWithBackoff(
        () => fetchItemsRef.current({
          category: category === "All" ? undefined : category,
          search: normalizedSearch && searchMode === 'server' ? normalizedSearch : undefined,
          limit: queryLimit,
        }),
        context,
        0,
        isActive
      )

      if (!isActive()) return false

      const nextItems = normalizedSearch
        ? (processSearchResultsRef.current
            ? processSearchResultsRef.current(queryItems, normalizedSearch)
            : defaultProcessSearchResults(queryItems, normalizedSearch))
        : queryItems

      setItems(nextItems)
      setVisibleItems(initialLimit)
      setHasMoreItems(
        searchMode === 'client' && normalizedSearch
          ? nextItems.length > initialLimit
          : queryItems.length >= queryLimit || nextItems.length > initialLimit
      )
      return true
    } catch (error) {
      if (isActive()) {
        console.error(`Error running ${context}:`, error)
      }
      return false
    } finally {
      if (isActive()) {
        setIsSearching(false)
        if (showLoading) setIsLoading(false)
      }
    }
  }, [initialLimit, retryWithBackoff, searchLimit, searchMode])

  // Handle search with debouncing. Incrementing the query id immediately
  // invalidates any in-flight result before the new debounce has elapsed.
  const handleSearch = useCallback((value: string) => {
    setSearchTerm(value)

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }

    const requestId = ++latestQueryIdRef.current
    setIsLoadingMore(false)

    if (!value.trim()) {
      setIsSearching(false)
      void executeQuery({
        category: selectedCategory,
        search: '',
        context: 'clear-search',
        requestId,
      })
      return
    }

    setIsSearching(true)
    searchTimeoutRef.current = setTimeout(() => {
      void executeQuery({
        category: selectedCategory,
        search: value,
        context: 'search',
        requestId,
      })
    }, searchDebounceMs)
  }, [executeQuery, searchDebounceMs, selectedCategory])

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current)
      }
      latestQueryIdRef.current += 1
    }
  }, [])

  // Handle category filter
  const handleCategoryFilter = useCallback(async (category: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }

    const requestId = ++latestQueryIdRef.current
    setSelectedCategory(category)
    setVisibleItems(initialLimit)

    if (searchTerm.trim()) setIsSearching(true)

    await executeQuery({
      category,
      search: searchTerm,
      context: 'category-filter',
      requestId,
    })
  }, [executeQuery, initialLimit, searchTerm])

  // Explicit atomic update for recovery controls that intentionally change both
  // dimensions, such as "search all products for fertilizer".
  const handleQueryChange = useCallback(async (category: string, search: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }

    const requestId = ++latestQueryIdRef.current
    setSelectedCategory(category)
    setSearchTerm(search)
    setVisibleItems(initialLimit)
    if (search.trim()) setIsSearching(true)

    await executeQuery({
      category,
      search,
      context: 'query-change',
      requestId,
    })
  }, [executeQuery, initialLimit])

  // Handle load more
  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore) return

    const requestId = latestQueryIdRef.current
    const isActive = () => latestQueryIdRef.current === requestId
    setIsLoadingMore(true)
    try {
      // If we still have unseen items that are already loaded, reveal them.
      // If that remainder is smaller than a full "page", top up by fetching more
      if (visibleItems < items.length) {
        const remaining = items.length - visibleItems
        const newVisibleCount = Math.min(visibleItems + loadMoreLimit, items.length)
        setVisibleItems(newVisibleCount)

        // Client-side search already holds the full matching result set. Once
        // its buffer is revealed there is no remote page to top up.
        if (searchMode === 'client' && searchTerm.trim()) {
          setHasMoreItems(items.length > newVisibleCount)
          return
        }

        if (remaining < loadMoreLimit) {
          const needed = loadMoreLimit - remaining
          const newItems = await retryWithBackoff(
            () => fetchItemsRef.current({
              category: selectedCategory === "All" ? undefined : selectedCategory,
              search: searchTerm.trim() ? searchTerm : undefined,
              limit: needed,
              offset: items.length
            }),
            'load-more',
            0,
            isActive
          )

          if (!isActive()) return

          if (newItems.length > 0) {
            // Drop any items already loaded so an overlapping page (offsets shift
            // when content is published or removed) can't create duplicate keys.
            const seen = new Set(items.map(it => it.id))
            const freshItems = newItems.filter(it => !seen.has(it.id))
            if (freshItems.length > 0) {
              setItems(prev => [...prev, ...freshItems])
              setVisibleItems(prev => prev + freshItems.length)
            }
            // A full top-up that was entirely duplicates can't advance the offset,
            // so stop instead of refetching the same overlap forever.
            setHasMoreItems(newItems.length === needed && freshItems.length > 0)
          } else {
            setHasMoreItems(false)
          }
        } else {
          // We still have buffered items; keep hasMore true if hidden items remain
          setHasMoreItems(items.length > newVisibleCount)
        }
        return
      }

      if (searchMode === 'client' && searchTerm.trim()) {
        setHasMoreItems(false)
        return
      }

      // Otherwise fetch the next page from the source
      const newItems = await retryWithBackoff(
        () => fetchItemsRef.current({
          category: selectedCategory === "All" ? undefined : selectedCategory,
          search: searchTerm.trim() ? searchTerm : undefined,
          limit: loadMoreLimit,
          offset: items.length
        }),
        'load-more',
        0,
        isActive
      )

      if (!isActive()) return

      if (newItems.length > 0) {
        // Drop any items already loaded so an overlapping page (offsets shift
        // when content is published or removed) can't create duplicate keys.
        const seen = new Set(items.map(it => it.id))
        const freshItems = newItems.filter(it => !seen.has(it.id))
        if (freshItems.length > 0) {
          setItems(prev => [...prev, ...freshItems])
          setVisibleItems(prev => prev + freshItems.length)
        }
        // A full page that was entirely duplicates can't advance the offset, so
        // stop instead of refetching the same overlap forever.
        setHasMoreItems(newItems.length === loadMoreLimit && freshItems.length > 0)
      } else {
        setHasMoreItems(false)
      }
    } catch (error) {
      if (!isActive()) return
      const err = error instanceof Error ? error : new Error(String(error))
      // Surface the error to any provided handler and mark state accordingly
      setErrorState({ hasError: true, error: err, context: 'load-more', retryCount: 0 })
      if (onError) {
        onError(err, 'load-more')
      }
      // Keep hasMoreItems true so the UI can offer a retry path
      console.error('Error loading more items:', err)
    } finally {
      if (isActive()) setIsLoadingMore(false)
    }
  }, [items, visibleItems, loadMoreLimit, isLoadingMore, selectedCategory, searchTerm, retryWithBackoff, onError, searchMode])

  // Clear all filters
  const clearFilters = useCallback(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }

    const requestId = ++latestQueryIdRef.current
    setSearchTerm("")
    setSelectedCategory("All")
    setVisibleItems(initialLimit)
    setIsSearching(false)

    void executeQuery({
      category: 'All',
      search: '',
      context: 'clear-filters',
      requestId,
    })
  }, [executeQuery, initialLimit])

  // Refetch the current query when an external refresh key changes.
  useEffect(() => {
    const previousKey = previousRefreshKeyRef.current
    const isFirstObservation = typeof previousKey === 'symbol'
    const keyChanged = previousKey !== refreshKey

    previousRefreshKeyRef.current = refreshKey

    if (!hasInitializedRef.current) return
    if (isFirstObservation || (!isFirstObservation && !keyChanged)) {
      return
    }

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
      searchTimeoutRef.current = undefined
    }

    const requestId = ++latestQueryIdRef.current
    void executeQuery({
      category: selectedCategory,
      search: searchTerm,
      context: 'refresh-key',
      requestId,
      showLoading: true,
    })
  }, [refreshKey, executeQuery, searchTerm, selectedCategory])
  
  // Retry load-more after a transient failure (preserves existing items and scroll position)
  const retryLoadMore = useCallback(() => {
    setErrorState({ hasError: false, error: null, context: '', retryCount: 0 })
    // handleLoadMore checks isLoadingMore internally, so just call it
    handleLoadMore()
  }, [handleLoadMore])

  // Retry function for manual retry
  const retry = useCallback(async () => {
    setErrorState({ hasError: false, error: null, context: '', retryCount: 0 })
    setIsLoading(true)
    const requestId = ++latestQueryIdRef.current

    const categoryRetry = categories.length === 0 && fetchCategoriesRef.current
      ? retryWithBackoff(
          () => fetchCategoriesRef.current!(),
          'retry-load-categories',
          0,
          () => latestQueryIdRef.current === requestId
        ).then((nextCategories) => {
          if (latestQueryIdRef.current === requestId) setCategories(nextCategories)
        })
      : Promise.resolve()

    setIsLoadingCategories(categories.length === 0 && Boolean(fetchCategoriesRef.current))
    await Promise.all([
      executeQuery({
        category: selectedCategory,
        search: searchTerm,
        context: 'retry-load-items',
        requestId,
        showLoading: true,
      }),
      categoryRetry,
    ])
    if (latestQueryIdRef.current === requestId) setIsLoadingCategories(false)
  }, [categories.length, executeQuery, retryWithBackoff, searchTerm, selectedCategory])

  return {
    items,
    categories,
    selectedCategory,
    searchTerm,
    isSearching,
    visibleItems,
    isLoadingMore,
    isLoading,
    isLoadingCategories,
    hasMoreItems,
    errorState,
    handleSearch,
    handleCategoryFilter,
    handleQueryChange,
    handleLoadMore,
    retryLoadMore,
    clearFilters,
    setCategories,
    retry
  }
} 
